import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

/**
 * The direction of travel, floating in the plate.
 *
 * No disc, no marker on the floor — just a chevron that drifts the way the walk
 * goes and takes you there when you click it. Position and direction are one
 * line per beat in lib/beats.js, so the arrow always sits on the part of the
 * founder's art you are actually walking into.
 */
const ROTATION = { in: 90, left: 180, right: 0, forward: 90 };
const DRIFT = {
  in: { y: [0, 11, 0] },
  forward: { y: [0, 11, 0] },
  left: { x: [0, -13, 0] },
  right: { x: [0, 13, 0] },
};

export const WalkArrow = ({ nav, onClick, tone = "var(--stage-gold)" }) => {
  if (!nav) return null;
  const dir = nav.dir || "in";

  return (
    <button
      type="button"
      data-testid="walk-arrow"
      aria-label={nav.label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="group absolute z-20 -translate-x-1/2 -translate-y-1/2"
      style={{ left: `${nav.x}%`, top: `${nav.y}%` }}
    >
      <motion.div
        className="flex flex-col items-center gap-2"
        animate={DRIFT[dir]}
        transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="relative grid place-items-center">
          {/* a breath of light so the chevron survives bright, busy plates —
              not a floor marker, just enough contrast to be findable */}
          <motion.span
            aria-hidden
            className="pointer-events-none absolute h-[130px] w-[130px] rounded-full"
            animate={{ opacity: [0.3, 0.6, 0.3], scale: [0.9, 1.05, 0.9] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
            style={{
              background:
                "radial-gradient(circle, rgba(10,8,4,0.62) 0%, rgba(10,8,4,0.34) 45%, rgba(10,8,4,0) 72%)",
            }}
          />
          <motion.span
            className="relative block transition-transform duration-300 group-hover:scale-125"
            style={{ rotate: `${ROTATION[dir]}deg`, color: tone }}
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
          >
            <ChevronRight
              size={76}
              strokeWidth={2}
              style={{
                filter:
                  "drop-shadow(0 0 10px rgba(255,232,178,0.95)) drop-shadow(0 0 34px rgba(224,164,74,0.85)) drop-shadow(0 2px 4px rgba(0,0,0,0.9))",
              }}
            />
          </motion.span>
        </div>
        <span
          className="tracked whitespace-nowrap text-[9px] text-white/45 transition-colors duration-300 group-hover:text-white/90"
          style={{ textShadow: "0 2px 14px rgba(0,0,0,0.95)" }}
        >
          {nav.label}
        </span>
      </motion.div>
    </button>
  );
};

export default WalkArrow;
